import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import cs from 'classnames';

import { generateParams } from 'features/filters/filtersSlice';
import jobseekersService from 'features/jobseekers/jobseekersService';
import { selectAppLocale } from 'features/locale/localeSelectors';

import useErrorCatch from 'hooks/useErrorCatch';
import { useInfiniteScrollPagination } from 'hooks/useInfiniteScrollPagination';
import usePageviewEvent from 'hooks/usePageViewEvent';

import { GTMEventLogger } from 'utils/analytics';

import { Filters } from 'types/filters';
import { JobseekerMatchesData, JobseekersMatches } from 'types/jobseekers';

import { GTMEvents } from 'constants/enums';
import { FALLBACK_VALUE } from 'constants/uiSettings';

import MatchCardMini from './MatchCardMini/MatchCardMini';
import styles from './MatchesView.module.scss';

interface MatchesViewProps {
  jobId: string | null;
}

const MatchesView = ({ jobId }: MatchesViewProps) => {
  const { t } = useTranslation();
  const [inviteSentIds, setInviteSentIds] = useState<string[]>([]);
  const catchError = useErrorCatch();
  const locale = useSelector(selectAppLocale);

  usePageviewEvent(GTMEvents.JobListViewSidebarMatching);

  const loadItems = useCallback(
    async (filters: Filters): Promise<JobseekersMatches | undefined> => {
      if (!jobId) {
        return;
      }

      const params = generateParams({ ...filters, locale });

      try {
        const result = await jobseekersService.getJobseekersMatchesJob(
          jobId,
          params
        );

        if (result?.content?.length) {
          GTMEventLogger(
            GTMEvents.AiJobsListViewSidebarMatchingRecommendationsLoaded
          );
        }

        return result || undefined;
      } catch (error) {
        catchError({
          error,
          default: {
            message: t('error.message.operationFailed'),
            description: t('error.message.failedFetchJobseekersMatches')
          }
        });

        return;
      }
    },
    [jobId, locale, catchError, t]
  );

  const { lastElementRef, data, isLoading } = useInfiniteScrollPagination<
    JobseekersMatches,
    JobseekerMatchesData
  >({
    errorMessage: t('error.message.failedFetchJobseekersMatches'),
    loadItems
  });

  const handleSetInviteSent = useCallback((jobseekerId: string) => {
    GTMEventLogger(GTMEvents.AiJobsListViewSidebarMatchingRecommendationInvite);
    setInviteSentIds((prev) =>
      prev.includes(jobseekerId) ? prev : [...prev, jobseekerId]
    );
  }, []);

  const preparedData = useMemo(
    () =>
      data.map((item) => ({
        ...item,
        inviteSent:
          Boolean(item.inviteSent) || inviteSentIds.includes(item.jobseekerId)
      })),
    [data, inviteSentIds]
  );

  return (
    <aside>
      <section
        className={cs(styles.container, 'matches-view-sidebar-container')}
      >
        {preparedData.map((match, index) => {
          const isLastElement = index === preparedData.length - 1;

          return (
            <div
              key={`${match.jobseekerId}-${match.jobId}`}
              ref={isLastElement ? lastElementRef : null}
            >
              <MatchCardMini data={match} onInviteSent={handleSetInviteSent} />
            </div>
          );
        })}
      </section>

      {isLoading && <div>{t('common.loading')}</div>}
      {!isLoading && !preparedData.length && (
        <div className={styles.noMatches}>
          {t('matches.noMatches') || FALLBACK_VALUE}
        </div>
      )}
    </aside>
  );
};

export default MatchesView;
